<script setup lang="ts">
import CashbackMenu from '~/components/sidebar/menu/CashbackMenu.vue';
import Sidebarv2MenuItem from '~/components/sidebarv2/MenuItem.vue';
import { useGiratudoStore } from '@/stores/apostatudo/giratudo';
import { useGiratudo } from '~/composables/apostatudo/use-giratudo';

interface Props {
  isLoggedUser?: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  onClickMenu: [];
  openRegisterModal: [];
}>();

const route = useRoute();
const { isSidebarMenuOpen, isLayoutMobileOrTablet } = useLayout();
const { isUserRefersComingSoon } = useFeatureFlagsJson();

const showCashbackMenu = ref(false);
const isCashbackModalOpen = ref(false);
let cashbackBtnRef: HTMLElement | undefined;

const useModalForCashback = isLayoutMobileOrTablet;

const throttledUpdate = () => {
  if (showCashbackMenu.value) updateMenuPosition();
};

const cashbackMenuStyle = ref<any>({ display: 'none' });

const updateMenuPosition = () => {
  if (!cashbackBtnRef) {
    cashbackMenuStyle.value = { display: 'none' };

    return;
  }

  const rect = cashbackBtnRef.getBoundingClientRect();
  const menuWidth = 192;
  const offset = 40;

  let left = rect.left + rect.width + offset;

  if (left + menuWidth > window.innerWidth) {
    left = rect.left - menuWidth - offset;
  }

  cashbackMenuStyle.value = {
    position: 'fixed',
    top: rect.top - 170 + 'px',
    left: left + 10 + 'px',
    zIndex: 10000,
  };
};

watch(showCashbackMenu, (newVal) => {
  if (newVal) {
    nextTick(() => updateMenuPosition());
  }
});

watch(isSidebarMenuOpen, () => {
  if (showCashbackMenu.value) {
    setTimeout(() => updateMenuPosition(), 300);
  }
});

function handleClickOutside() {
  showCashbackMenu.value = false;
}

function openCashback() {
  if (useModalForCashback.value) {
    isCashbackModalOpen.value = true;
  } else {
    showCashbackMenu.value = true;
  }
}

function handleCashbackSelect(url: string) {
  showCashbackMenu.value = false;
  isCashbackModalOpen.value = false;
  emit('onClickMenu');
  navigateTo(url);
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  document.addEventListener('scroll', throttledUpdate, {
    passive: true,
    capture: true,
  });
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  document.removeEventListener('scroll', throttledUpdate, { capture: true });
});

// Abertura via URL (?open=cashback) e via shouldOpenCashback (ex.:
// MenuHorizontalApostatudo) é tratada centralmente na layout. Aqui só
// reagimos a clique direto no botão da sidebar (`openCashback`).

function isMenuItemExactActive(path: string) {
  if (!path) return false;

  return route.fullPath === path;
}

// Giratudo
const giratudoStore = useGiratudoStore();
const { fetchStatus: fetchGiratudoStatus } = useGiratudo();
const { openLogin } = useAuth();

const giratudoBannerUrl = computed(() =>
  getAssetsUrlForCDN('/assets/images/giratudo/banner-sidebar.png'),
);

// Countdown funcional - conta até raffleEndTime ou próxima hora cheia
const giratudoCountdown = ref('00:00:00');
let countdownInterval: ReturnType<typeof setInterval> | null = null;

function updateCountdown() {
  const now = new Date();
  const endRaw = giratudoStore.raffleEndTime;
  let end = endRaw ? new Date(endRaw) : null;

  // Se não tem end time ou já passou, usa a próxima hora cheia
  if (!end || end.getTime() <= now.getTime()) {
    const nextHour = new Date(now);

    nextHour.setHours(nextHour.getHours() + 1, 0, 0, 0);
    end = nextHour;
  }

  const diff = Math.max(0, end.getTime() - now.getTime());
  const hours = Math.floor(diff / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);

  giratudoCountdown.value = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

onMounted(() => {
  updateCountdown();
  countdownInterval = setInterval(updateCountdown, 1000);

  // Busca o status inclusive para deslogados: o flag global de
  // participação (manutenção) não depende de user_id.
  fetchGiratudoStatus();
});

// Quando o usuário faz login, buscar status do giratudo
watch(
  () => props.isLoggedUser,
  (logged) => {
    if (logged) {
      fetchGiratudoStatus();
    }
  },
);

onUnmounted(() => {
  if (countdownInterval) {
    clearInterval(countdownInterval);
    countdownInterval = null;
  }
});

function handleGiratudoClick() {
  if (!props.isLoggedUser) {
    openLogin();

    return;
  }
  giratudoStore.open();
  emit('onClickMenu');
}

// Quick action buttons (Indique, Código, App)
const quickActions = [
  {
    id: 'indique',
    label: 'Indique',
    icon: '/assets/images/menu-lateral-grid/refers.webp',
    img: '/assets/images/giratudo/indique.png',
    url: '/user/refers',
    auth: true,
  },
  {
    id: 'codigo',
    label: 'Código',
    icon: '/assets/images/menu-lateral-grid/promocode.webp',
    img: '/assets/images/giratudo/codigo.png',
    url: '/promocode',
    auth: true,
  },
  {
    id: 'app',
    label: 'App',
    icon: '/assets/images/menu-lateral-grid/baixe-app.png',
    img: '/assets/images/giratudo/app.png',
    url: '/promotions/aplicativo',
    auth: false,
  },
];

function handleQuickActionClick(item: (typeof quickActions)[number]) {
  // Indique e Ganhe "Em breve": quick action travado, click desativado.
  if (item.id === 'indique' && isUserRefersComingSoon.value) {
    return;
  }

  if (!props.isLoggedUser && item.auth) {
    openLogin();

    return;
  }
  navigateTo(item.url);
  emit('onClickMenu');
}

// Casino menu items (Atalhos de Cassino)
const casinoMenuItems = [
  {
    id: 'todos-os-jogos',
    label: 'Todos os jogos',
    url: '/games/category/all',
    icon: 'menu-lateral/todos-os-jogos',
    glowColor: '#0606ad',
  },
  {
    id: 'promotions',
    label: 'Promoções',
    url: '/promotions',
    icon: 'menu-lateral/bullhorn',
    glowColor: '#0606ad',
  },
  {
    id: 'destaques',
    label: 'Destaques oficiais',
    url: '/perfil-oficial',
    icon: 'menu-lateral/destaques-oficiais',
    glowColor: '#0606ad',
  },
  {
    id: 'jogos-ao-vivo',
    label: 'Jogos ao vivo',
    url: '/games/category/all?initialCategory=jogos-ao-vivo',
    icon: 'menu-lateral/jogos-ao-vivo',
    glowColor: '#0606ad',
  },
  {
    id: 'roletas',
    label: 'Roletas',
    url: '/games/category/all?initialCategory=roletas',
    icon: 'menu-lateral/roletas',
    glowColor: '#0606ad',
  },
  {
    id: 'aviators',
    label: 'Aviators',
    url: '/perfil-oficial/aviators',
    icon: 'menu-lateral/aviators',
    glowColor: '#0606ad',
  },
  {
    id: 'slots',
    label: 'Slots',
    url: '/games/category/all?initialCategory=slots',
    icon: 'menu-lateral/slots',
    glowColor: '#0606ad',
  },
  {
    id: 'provedoras',
    label: 'Provedoras',
    url: '/games/providers',
    icon: 'menu-lateral/provedoras',
    glowColor: '#0606ad',
  },
];

const showNormalGrid = computed(() => isSidebarMenuOpen.value);
const showClosedContent = computed(() => !isSidebarMenuOpen.value);

// Overlay do banner: nunca exibe "Girar roleta agora!".
// - Deslogado fora de manutenção: só o banner (sem overlay).
// - Logado: "Ver ranking" (ou "Em manutenção").
// - Manutenção é flag global, então aparece inclusive para deslogado.
const showGiratudoOverlay = computed(
  () => props.isLoggedUser || !giratudoStore.participationEnabled,
);

// Re-fetch status quando o modal do giratudo fecha (usuario pode ter girado)
watch(
  () => giratudoStore.isOpen,
  (isOpen) => {
    if (!isOpen && props.isLoggedUser) {
      fetchGiratudoStatus();
    }
  },
);
</script>

<template>
  <!-- GIRATUDO BANNER -->
  <div
    v-if="showNormalGrid"
    class="giratudo-banner"
    :class="{ 'giratudo-banner--no-overlay': !showGiratudoOverlay }"
    @click="handleGiratudoClick"
  >
    <NuxtImg
      alt=""
      class="giratudo-banner-bg"
      draggable="false"
      :src="getAssetsUrlForCDN('/assets/images/giratudo/banner-bg.png')"
    />
    <NuxtImg
      alt="Giratudo"
      class="giratudo-banner-logo"
      draggable="false"
      :src="giratudoBannerUrl"
    />
    <div
      v-if="showGiratudoOverlay"
      class="giratudo-banner-overlay"
    >
      <template v-if="!giratudoStore.participationEnabled">
        <span class="giratudo-label">Em manutenção</span>
      </template>
      <template v-else>
        <span class="giratudo-label">Ver ranking</span>
        <div class="giratudo-timer-badge">
          <NuxtIcon
            class="giratudo-clock-icon"
            :filled="true"
            name="clock-giratudo"
          />
          <span class="giratudo-countdown">{{ giratudoCountdown }}</span>
        </div>
      </template>
    </div>
  </div>

  <!-- Giratudo fechado -->
  <div
    v-if="showClosedContent"
    class="px-2.5 bg-transparent w-[70px]"
  >
    <div
      v-tooltip.desktopOnly="'Giratudo'"
      class="grid-item flex justify-center items-center border rounded-md border-solid border-[rgba(255,255,255,0.10)] transition-colors relative min-h-[48px] cursor-pointer"
      @click="handleGiratudoClick"
    >
      <img
        alt="Giratudo"
        class="h-[39px] object-contain"
        draggable="false"
        :src="giratudoBannerUrl"
      />
    </div>
  </div>

  <!-- QUICK ACTIONS (Indique, Código, App) -->
  <div
    v-if="showNormalGrid"
    class="quick-actions-row"
  >
    <div
      v-for="item in quickActions"
      :key="item.id"
      class="quick-action-btn-wrapper"
      @click="handleQuickActionClick(item)"
    >
      <NuxtImg
        :alt="item.label"
        class="quick-action-btn"
        draggable="false"
        loading="eager"
        :src="getAssetsUrlForCDN(item.img)"
      />
      <BaseComingSoonOverlay
        v-if="item.id === 'indique' && isUserRefersComingSoon"
        rounded="rounded-[11px]"
      />
    </div>
  </div>

  <!-- Quick actions fechado -->
  <div
    v-if="showClosedContent"
    class="px-2.5 bg-transparent w-[70px]"
  >
    <div class="grid grid-cols-1 gap-[8px] max-w-[50px]">
      <div
        v-for="item in quickActions"
        :key="item.id"
        v-tooltip.desktopOnly="item.label"
        class="grid-item flex justify-center items-center border rounded-md border-solid border-[rgba(255,255,255,0.10)] transition-colors relative min-h-[48px] cursor-pointer"
        @click="handleQuickActionClick(item)"
      >
        <NuxtImg
          :alt="item.label"
          class="h-[39px] object-contain"
          draggable="false"
          height="39"
          loading="eager"
          :src="getAssetsUrlForCDN(item.icon)"
          width="39"
        />
        <div
          v-if="!props.isLoggedUser && item.auth"
          class="absolute top-1 right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center"
        >
          🔒
        </div>
        <BaseComingSoonOverlay
          v-if="item.id === 'indique' && isUserRefersComingSoon"
          rounded="rounded-md"
        />
      </div>
    </div>
  </div>

  <!-- CASHBACK BUTTON -->
  <div
    v-if="showNormalGrid"
    :ref="
      (el) => {
        cashbackBtnRef = el as HTMLElement;
      }
    "
    class="px-[15px]"
    data-cashback-btn
    @click.stop="openCashback"
  >
    <NuxtImg
      alt="Cashback"
      class="cashback-btn-img"
      draggable="false"
      loading="lazy"
      :src="getAssetsUrlForCDN('/assets/images/giratudo/cashback.png')"
    />
  </div>

  <!-- Cashback fechado -->
  <template v-if="showClosedContent">
    <button
      :ref="
        (el) => {
          cashbackBtnRef = el as HTMLElement;
        }
      "
      v-tooltip.desktopOnly="'Cashback'"
      class="flex h-[48px] w-[48px] items-center justify-center gap-0 backdrop-blur-[5.922131061553955px] rounded-[6px] border-[1.184px] border-solid border-[rgba(255,255,255,0.10)] relative cursor-pointer select-none mx-2.5"
      data-cashback-btn
      type="button"
      @click.stop="openCashback"
    >
      <NuxtImg
        alt="Cashback"
        class="w-full"
        draggable="false"
        height="48"
        loading="eager"
        :src="
          getAssetsUrlForCDN(
            '/assets/images/menu-lateral-grid/cashback-small.png',
          )
        "
        width="48"
      />
    </button>
  </template>

  <!-- ATALHOS DE CASSINO -->
  <div
    v-if="showNormalGrid"
    class="casino-shortcuts-section"
  >
    <span class="section-title">Atalhos de Cassino</span>

    <div class="casino-shortcuts-container">
      <template
        v-for="{ id, label, url, icon, glowColor } in casinoMenuItems"
        :key="id"
      >
        <Sidebarv2MenuItem
          v-tooltip.desktopOnly.closedSideBarOnly="label"
          :draggable="false"
          :glow-color="glowColor"
          :is-active="isMenuItemExactActive(localeLink(url) as string)"
          :to="localeLink(url)"
          @click="emit('onClickMenu')"
        >
          <NuxtIcon
            class="sidebarIcon"
            :name="icon"
          />
          <span>{{ label }}</span>
        </Sidebarv2MenuItem>
      </template>
    </div>
  </div>

  <!-- Atalhos fechado -->
  <div
    v-if="showClosedContent"
    :class="[
      'sidebar-toggle flex flex-col justify-center items-start self-stretch p-0 w-[70px] rounded-none mx-0 border-0',
    ]"
  >
    <template
      v-for="{ id, label, url, icon } in casinoMenuItems"
      :key="id"
    >
      <Sidebarv2MenuItem
        v-tooltip.desktopOnly.closedSideBarOnly="label"
        :draggable="false"
        :is-active="isMenuItemExactActive(localeLink(url) as string)"
        :to="localeLink(url)"
        @click="emit('onClickMenu')"
      >
        <NuxtIcon
          class="sidebarIcon"
          :name="icon"
        />
        <span>{{ label }}</span>
      </Sidebarv2MenuItem>
    </template>
  </div>

  <!-- Menu Cashback desktop -->
  <CashbackMenu
    v-if="showCashbackMenu && !useModalForCashback"
    :is-open="showCashbackMenu"
    :style="cashbackMenuStyle as any"
    @close="showCashbackMenu = false"
    @select-item="handleCashbackSelect"
  />

  <!-- Menu Cashback mobile -->
  <CashbackMenu
    v-else-if="useModalForCashback"
    :is-open="isCashbackModalOpen"
    @close="isCashbackModalOpen = false"
    @select-item="handleCashbackSelect"
  />
</template>

<style lang="scss">
/* Giratudo Banner */
.giratudo-banner {
  @apply mx-[15px] cursor-pointer relative;
  margin-top: 50px;

  @media (hover: hover) {
    &:hover {
      filter: brightness(1.1);
    }
  }

  .giratudo-banner-bg {
    @apply w-full block pointer-events-none select-none rounded-[13px];
    aspect-ratio: 250 / 100;
    height: auto;
    object-fit: contain;
    object-position: center top;
    margin-top: -13px;
  }

  /* Sem overlay (deslogado fora de manutenção): a imagem assume sua
     proporção natural e preenche todo o card, sem a faixa vazia que a
     barra de 40px ocupava. */
  &.giratudo-banner--no-overlay .giratudo-banner-bg {
    aspect-ratio: auto;
    object-fit: fill;
  }

  .giratudo-banner-logo {
    @apply absolute pointer-events-auto select-none;
    width: 80%;
    left: 50%;
    transform: translateX(-50%);
    top: -50px;
    z-index: 1;
    cursor: pointer;

    @media screen and (min-width: 1024px) {
      width: 90%;
    }
  }

  .giratudo-banner-overlay {
    @apply absolute bottom-0 left-0 right-0 flex items-center justify-center gap-[12px] px-4 rounded-b-[13px];
    height: 40px;
    align-items: center;
    background: rgba(0, 34, 255, 0.7);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    z-index: 2;
  }

  .giratudo-label {
    @apply text-white;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 14px;
    font-weight: 600;
    line-height: 40px;
  }

  .giratudo-timer-badge {
    display: inline-flex;
    padding: 3px 6.822px;
    align-items: center;
    justify-content: center;
    align-self: center;
    gap: 6px;
    border-radius: 5.685px;
    background: rgba(255, 255, 255, 0.15);
    color: white;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 400;
    font-size: 12px;
  }

  .giratudo-clock-icon {
    @apply text-[16px];
    transform: none;
    color: white !important;

    svg {
      color: white !important;
      fill: white !important;
    }
  }

  .giratudo-countdown {
    @apply tabular-nums;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 700;
    width: 65px;
    display: inline-block;
    text-align: center;
  }
}

/* Quick Actions Row */
.quick-actions-row {
  @apply flex gap-[6px] px-[15px];

  .quick-action-btn-wrapper {
    @apply relative cursor-pointer select-none overflow-hidden rounded-[11px];
    width: calc((100% - 12px) / 3);
    flex-shrink: 0;
  }

  .quick-action-btn {
    @apply block w-full cursor-pointer select-none rounded-[11px];
    height: auto;

    @media (hover: hover) {
      &:hover {
        filter: brightness(1.1);
      }
    }
  }
}

/* Cashback Button */
.cashback-btn-img {
  @apply w-full cursor-pointer select-none rounded-[11px] -mt-[8px];

  @media (hover: hover) {
    &:hover {
      filter: brightness(1.1);
    }
  }
}

/* Casino Shortcuts Section */
.casino-shortcuts-section {
  @apply px-[15px] flex flex-col gap-[5px];

  .section-title {
    @apply font-semibold text-[13px];
    color: #939393;
  }

  .casino-shortcuts-container {
    @apply flex flex-col gap-[4px];
  }
}

.sidebar-toggle {
  @apply border-solid border-b border-sidebar-links/10 select-none;
}

@media (hover: hover) {
  .grid-item:hover {
    background-color: rgba(71, 85, 105, 0.7);
  }
}
</style>
